import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { NotifyTimeTypeTranslationKeys, RMMAlertTypes } from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { pushRecipientValidator } from 'mbs-ui-shared@utils/push-recipient-validator';
import { recipientValidator } from 'mbs-ui-shared@utils/recipient-validator';
import { RmmCustomAlertingServiceController } from '../../rmm-custom-alerting-module/services/rmm-custom-alerting-controller.service';

export enum ActionTypes {
  GLOBAL = 'global',
  COMPANY = 'company',
  COMPUTER = 'computer',
  CUSTOM = 'custom'
}

export enum NotifyTimeType {
  Hours = 'Hour(s)',
  Minutes = 'Minute(s)'
}

@Injectable()
export default class EditRmmNotificationUtility {
  private readonly minutesTimeType = this.i18next.transform(NotifyTimeTypeTranslationKeys.Minutes);
  constructor(private fb: FormBuilder, private customAlertService: RmmCustomAlertingServiceController, private i18next: I18NextPipe) {}

  // Notification Utility
  getDefaultAlertCustomisationForm() {
    return this.fb.group({
      recipient: new FormControl({ rule: 0, emails: [] }, recipientValidator.bind(this)),
      pushRecipient: new FormControl({ type: 0, users: [] }, pushRecipientValidator.bind(this)),
      pushNotification: new UntypedFormGroup({ enabled: new FormControl(false) }),
      [RMMAlertTypes.HighCpuUsage]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.HighCpuUsage)),
      [RMMAlertTypes.HighMemoryUsage]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.HighMemoryUsage)),
      [RMMAlertTypes.LowDiskSpace]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.LowDiskSpace)),
      [RMMAlertTypes.SmartNotHealthy]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.SmartNotHealthy)),
      [RMMAlertTypes.SoftwareChanges]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.SoftwareChanges)),
      [RMMAlertTypes.HardwareChanges]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.HardwareChanges)),
      [RMMAlertTypes.ConnectionLoss]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.ConnectionLoss)),
      [RMMAlertTypes.NoAntivirus]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.NoAntivirus)),
      [RMMAlertTypes.NotUpdated]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.NotUpdated)),
      [RMMAlertTypes.SnmpDevice]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.SnmpDevice)),
      [RMMAlertTypes.ServiceState]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.ServiceState)),
      [RMMAlertTypes.HighTemperature]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.HighTemperature)),
      [RMMAlertTypes.WrongSystemTime]: this.fb.group(this.getParamsByAlertType(RMMAlertTypes.WrongSystemTime))
    });
  }

  public getParamsByAlertType(alertType: RMMAlertTypes) {
    const defaultTimeMeters = {
      updateDeferNumber: 5,
      updateDeferType: this.minutesTimeType
    };

    switch (alertType) {
      case RMMAlertTypes.HighCpuUsage:
      case RMMAlertTypes.HighMemoryUsage:
      case RMMAlertTypes.LowDiskSpace:
      case RMMAlertTypes.NotUpdated:
      case RMMAlertTypes.SmartNotHealthy:
      case RMMAlertTypes.HighTemperature:
        return {
          ...this.customAlertService.getDefaultParamsByAlertType(alertType),
          ...defaultTimeMeters
        };
      case RMMAlertTypes.SoftwareChanges:
      case RMMAlertTypes.HardwareChanges:
      case RMMAlertTypes.SnmpDevice:
      case RMMAlertTypes.ServiceState:
        return {
          ...this.customAlertService.getDefaultParamsByAlertType(alertType)
        };
      case RMMAlertTypes.WrongSystemTime:
        return {
          ...this.customAlertService.getDefaultParamsByAlertType(alertType),
          ...{ updateDeferNumberDiff: 5, updateDeferTypeDiff: this.minutesTimeType }
        };
      case RMMAlertTypes.ConnectionLoss:
        return {
          ...this.customAlertService.getDefaultParamsByAlertType(alertType),
          ...defaultTimeMeters,
          updateDeferNumber: 20
        };
      case RMMAlertTypes.NoAntivirus:
        return {
          ...this.customAlertService.getDefaultParamsByAlertType(alertType),
          ...defaultTimeMeters,
          updateDeferNumber: 15
        };
      default:
        return {
          enabled: false
        };
    }
  }
}
